import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const API_URL = process.env.REACT_APP_API_URL;

// Types/interfaces as before
interface Company {
  id: number | string;
  company_name: string;
  address?: string;
  pancard?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  gst_uin?: string;
  code?: string;
  cin?: string;
  email?: string;
  HSN_SAC?: string;
  GST_SLAB?: string;
  signature?: string;
  separatorsign?: string;
  prefix?: string;
  postfix?: string;
  [key: string]: any;
}

interface Customer {
  id: number | string;
  company_name: string;
  address?: string;
  city?: string;
  state?: string;
  gst_number?: string;
  gst_uin?: string;
  createdBy?: string | number; // for filter logic
  [key: string]: any;
}

interface BankDetail {
  id: number;
  company_id: number;
  bank_name: string;
  branch?: string;
  account_number: string;
  ifsc_code?: string;
  micr_code?: string;
  swift_code?: string;
  account_type?: string;
  account_holder_name?: string;
}

interface Setting {
  IGST: number;
  CGST: number;
  SGST: number;
  InvoiceSN?: string;
}

interface InvoiceRow {
  particulars: string;
  hsn_sac: string;
  amount: string;
  isCustom?: boolean;
}

const initialInvoiceRow: InvoiceRow = {
  particulars: "",
  hsn_sac: "",
  amount: "",
};

function parseHSNSAC(data: string | undefined): string[] {
  if (!data) return [];
  try {
    const arr = JSON.parse(data);
    if (Array.isArray(arr)) return arr;
    return [];
  } catch (err) {
    return [];
  }
}

function parseGSTSLAB(data: string | undefined): string[] {
  if (!data) return [];
  try {
    const arr = JSON.parse(data);
    if (Array.isArray(arr)) return arr;
    return [];
  } catch (err) {
    return [];
  }
}

function getInvoiceCode(companyName: string) {
  if (!companyName) return '';
  const matches = companyName.match(/\b\w/g) || [];
  return matches.join('').toUpperCase();
}

function getMonthName(date: string) {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleString("default", { month: "long" });
}

function getFinancialYear(date: string) {
  if (!date) return "";
  const d = new Date(date);
  let year = d.getFullYear();
  let month = d.getMonth(); // 0-indexed
  let prev, curr;
  if (month >= 3) {
    prev = String(year).slice(-2);
    curr = String(year + 1).slice(-2);
  } else {
    prev = String(year - 1).slice(-2);
    curr = String(year).slice(-2);
  }
  return `${prev}-${curr}`;
}

function padNumber(num: number, len = 3) {
  return num.toString().padStart(len, "0");
}

function formatDateToDDMMYYYY(dateString?: string) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (String(d) === "Invalid Date") return dateString;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// Amount in words helper (basic, for INR, does not handle paise)
function numberToWords(num: number): string {
  if (isNaN(num)) return "";
  if (num === 0) return "Zero";
  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const b = [
    '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'
  ];
  const g = [
    '', 'Thousand', 'Lakh', 'Crore', 'Arab', 'Kharab'
  ];
  let s = '';
  const n = Math.floor(num);
  let numStr = n.toString();
  let digits = numStr.split('').reverse().map(Number);

  function _inWordsHundred(n: number) {
    let str = '';
    if (n > 99) {
      str += a[Math.floor(n / 100)] + ' Hundred ';
      n = n % 100;
    }
    if (n > 19) {
      str += b[Math.floor(n / 10)] + ' ' + a[n % 10];
    } else {
      str += a[n];
    }
    return str.trim();
  }

  let i = 0;
  let words = [];
  let segment;

  let remaining = n;
  if (remaining === 0) return "Zero";
  while (remaining > 0 && i < g.length) {
    if (i === 0) {
      segment = remaining % 1000;
      remaining = Math.floor(remaining / 1000);
    } else {
      segment = remaining % 100;
      remaining = Math.floor(remaining / 100);
    }
    if (segment) words.push(_inWordsHundred(segment) + (g[i] ? ' ' + g[i] : ''));
    i++;
  }

  s = words.reverse().join(' ').replace(/\s+/g, ' ').trim();
  s = s.charAt(0).toUpperCase() + s.slice(1);
  return s;
}

// ------------------ NEW: Invoice No Separator & Composed Format Helpers ------------------

function computeFinalInvoiceNo({
  company,
  invoiceNo,
  invoiceDate,
  invoiceTotalCount,
  addMonthInInvoiceNo,
  separator,
  settings
}: {
  company: Company | undefined;
  invoiceNo: string;
  invoiceDate: string;
  invoiceTotalCount: number;
  addMonthInInvoiceNo: boolean;
  separator: string;
  settings: Setting | null;
}): string {
  if (!company) return "";
  let sep = (company.separatorsign && company.separatorsign.length > 0)
    ? company.separatorsign
    : "-";
  if (addMonthInInvoiceNo && invoiceDate) {
    let segs: string[] = [];
    if (company.prefix) segs.push(company.prefix);
    segs.push(getMonthName(invoiceDate));
    if (company.postfix) segs.push(company.postfix);
    if (invoiceNo) segs.push(invoiceNo);
    return segs.join(sep);
  } else {
    let segs: string[] = [];
    if (company.prefix) segs.push(company.prefix);
    if (invoiceNo) segs.push(invoiceNo);
    if (company.postfix) segs.push(company.postfix);
    return segs.join(sep);
  }
}

const InvoicePreviewContent: React.FC<{
  companies: Company[];
  companySignatureUrls: { [key: string]: string };
  customers: Customer[];
  invoicePreviewData: {
    companyId: string | number;
    customerId: string | number;
    invoiceNo: string;
    invoiceDate: string;
    rows: InvoiceRow[];
    sgstAmount: number;
    cgstAmount: number;
    igstAmount: number;
    totalAmount: number;
    selectedGstRate: string;
    remarks?: string;
    composedInvoiceNo?: string;
    bankDetails?: BankDetail | null;
    shipToAddress?: string;
    invoice_type?: string;
    gstIncluded?: boolean;
    lut_number?: string;
    commission_rate?: string;
    commission_base_amount?: string;
  };
}> = ({ companies, companySignatureUrls, customers, invoicePreviewData }) => {
  // ...unchanged...
  function getTaxTableData() {
    if (!invoicePreviewData) return {
      taxRows: [],
      totalTax: 0,
      uniqueHSN: []
    };
    const uniqueHSN: string[] = [
      ...new Set(invoicePreviewData.rows.map(r => r.hsn_sac).filter(Boolean))
    ];
    const result = uniqueHSN.map(hsn => {
      const hsnRows = invoicePreviewData.rows.filter(r => r.hsn_sac === hsn);
      const rawSum = hsnRows.reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0);
      const gstRateNum = Number(invoicePreviewData.selectedGstRate) || 0;
      const taxableValue = invoicePreviewData.gstIncluded ? (rawSum / (1 + gstRateNum / 100)) : rawSum;
      const igstRate = invoicePreviewData.igstAmount > 0 ? gstRateNum : 0;
      const sgstRate = invoicePreviewData.sgstAmount > 0 ? (gstRateNum / 2) : 0;
      const utgstRate = sgstRate;
      const igstAmount = igstRate ? (igstRate * taxableValue) / 100 : 0;
      const sgstAmount = sgstRate ? (sgstRate * taxableValue) / 100 : 0;
      const totalTax = igstAmount + 2 * sgstAmount;
      return {
        hsn,
        taxableValue: Math.round(taxableValue),
        igstRate,
        igstAmount: Math.round(igstAmount),
        sgstRate,
        sgstAmount: Math.round(sgstAmount),
        totalTax: Math.round(totalTax)
      };
    });

    const totalTax = result.reduce((sum, row) => sum + row.totalTax, 0);

    return {
      taxRows: result,
      totalTax,
      uniqueHSN
    };
  }
  const amountInWords =
    invoicePreviewData
      ? `INR ${numberToWords(Number(invoicePreviewData.totalAmount)).replace(/\s+/, ' ').trim()} Only`
      : "";

  const taxTableData = getTaxTableData();
  const taxAmountInWords = taxTableData.totalTax > 0
    ? `INR ${numberToWords(taxTableData.totalTax).replace(/\s+/, ' ').trim()} Only`
    : "";

  const finalInvoiceNo =
    invoicePreviewData.composedInvoiceNo
      ? invoicePreviewData.composedInvoiceNo
      : invoicePreviewData.invoiceNo;

  // ...return JSX unchanged...
  return (
    // ...same as before, unchanged...
    <div id="invoice-print" className="p-2 text-[11.5px] text-gray-800">
      <div
        className="text-center py-4"
        style={{
          fontSize: '22px',
          fontWeight: 800,
          letterSpacing: '1px',
          color: '#1e3a8a',
          paddingTop: '24px',
          paddingBottom: '18px'
        }}
      >
        {invoicePreviewData.invoice_type === 'proforma' ? 'PROFORMA INVOICE' : invoicePreviewData.invoice_type === 'lut' ? 'LUT INVOICE' : invoicePreviewData.invoice_type === 'commission' ? 'COMMISSION INVOICE' : 'TAX INVOICE'}
      </div>
      {/* LUT declaration note */}
      {invoicePreviewData.invoice_type === 'lut' && (
        <div className="text-center py-1 px-3 bg-amber-50 border border-amber-200 rounded-lg mb-2" style={{ fontSize: '10px' }}>
          <span className="font-bold text-amber-900">Supply meant for export under Bond or Letter of Undertaking without payment of IGST</span>
          {invoicePreviewData.lut_number && (
            <span className="ml-2 text-amber-800">| LUT No: <b>{invoicePreviewData.lut_number}</b></span>
          )}
        </div>
      )}
      {/* Commission invoice note */}
      {invoicePreviewData.invoice_type === 'commission' && invoicePreviewData.commission_rate && (
        <div className="text-center py-1 px-3 bg-purple-50 border border-purple-200 rounded-lg mb-2" style={{ fontSize: '10px' }}>
          <span className="font-bold text-purple-900">Commission Rate: {invoicePreviewData.commission_rate}%</span>
          {invoicePreviewData.commission_base_amount && (
            <span className="ml-2 text-purple-800">| Base Value: ₹ {Number(invoicePreviewData.commission_base_amount).toLocaleString('en-IN')}</span>
          )}
        </div>
      )}
      <table className="w-full border border-blue-300 border-collapse rounded-2xl shadow mb-2">
        <tbody>
          <tr className="invoice__header">
            <td className="border p-2 w-1/2 align-top">
              <div className="mb-1 text-xl text-blue-900 font-extrabold tracking-wide" style={{ fontSize: '14.5px', lineHeight: '1.2' }}>
                {
                  companies.find(
                    (c) => String(c.id) === String(invoicePreviewData.companyId)
                  )?.company_name
                }
              </div>
              <div className="mb-1 text-gray-700" style={{ fontSize: '11.5px' }}>
                <b>Address:</b>{" "}
                {
                  companies.find(
                    (c) => String(c.id) === String(invoicePreviewData.companyId)
                  )?.address
                }
              </div>
              <div className="mb-1 text-gray-700" style={{ fontSize: '11.5px' }}>
                <b>GSTIN:</b>{" "}
                {
                  companies.find(
                    (c) => String(c.id) === String(invoicePreviewData.companyId)
                  )?.gst_number
                }
              </div>
              <div className="mb-1 text-gray-700" style={{ fontSize: '11.5px' }}>
                <b>State:</b>{" "}
                {
                  companies.find(
                    (c) => String(c.id) === String(invoicePreviewData.companyId)
                  )?.state
                }
              </div>
              <div className="mb-1 text-gray-700" style={{ fontSize: '11.5px' }}>
                <b>CIN:</b>{" "}
                {
                  companies.find(
                    (c) => String(c.id) === String(invoicePreviewData.companyId)
                  )?.cin
                }
              </div>
              <div className="mb-1 text-gray-700" style={{ fontSize: '11.5px' }}>
                <b>Email:</b>{" "}
                {
                  companies.find(
                    (c) => String(c.id) === String(invoicePreviewData.companyId)
                  )?.email
                }
              </div>
            </td>
            <td className="border p-2 align-top bg-gradient-to-t from-blue-50 to-blue-100 flex flex-row">
              <div className="mb-1 flex flex-col" style={{ fontSize: '12px', minWidth: '135px' }}>
                <b>Invoice No:</b>{" "} <span className="text-blue-700">{finalInvoiceNo}</span>
              </div>
              <div className="mb-1 flex flex-col ml-8" style={{ fontSize: '12px', minWidth: '110px' }}>
                <b>Dated:</b> <span className="text-blue-700">{formatDateToDDMMYYYY(invoicePreviewData.invoiceDate)}</span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="border p-2 w-1/2 align-top bg-gradient-to-l from-pink-50 to-white/90" style={{ fontSize: '11.5px' }}>
              <b className="text-blue-800">Buyer (Bill to)</b>
              <div className="" />
              <div className="mb-1 mt-1">
                <b>
                  {
                    customers.find(
                      (c) => String(c.id) === String(invoicePreviewData.customerId)
                    )?.company_name
                  }
                </b>
              </div>
              <div className="mb-1"><b>Address:</b>{" "}
                {
                  customers.find(
                    (c) => String(c.id) === String(invoicePreviewData.customerId)
                  )?.address
                }
              </div>
              <div className="mb-1"><b>City:</b>{" "}
                {
                  customers.find(
                    (c) => String(c.id) === String(invoicePreviewData.customerId)
                  )?.city
                }
              </div>
              <div className="mb-1"><b>State:</b>{" "}
                {
                  customers.find(
                    (c) => String(c.id) === String(invoicePreviewData.customerId)
                  )?.state
                }
              </div>
              <div className="mb-1"><b>GSTIN:</b>{" "}
                {
                  customers.find(
                    (c) => String(c.id) === String(invoicePreviewData.customerId)
                  )?.gst_number
                }
              </div>
            </td>
            {/* Ship To section */}
            <td className="border p-2 w-1/2 align-top bg-white" style={{ fontSize: '11.5px' }}>
              <b className="text-blue-800">Consignee (Ship to)</b>
              {invoicePreviewData.shipToAddress ? (
                <div className="mt-1" style={{ whiteSpace: 'pre-line' }}>{invoicePreviewData.shipToAddress}</div>
              ) : (
                <>
                  <div className="mb-1 mt-1">
                    <b>
                      {
                        customers.find(
                          (c) => String(c.id) === String(invoicePreviewData.customerId)
                        )?.company_name
                      }
                    </b>
                  </div>
                  <div className="mb-1"><b>Address:</b>{" "}
                    {
                      customers.find(
                        (c) => String(c.id) === String(invoicePreviewData.customerId)
                      )?.address
                    }
                  </div>
                  <div className="mb-1"><b>City:</b>{" "}
                    {
                      customers.find(
                        (c) => String(c.id) === String(invoicePreviewData.customerId)
                      )?.city
                    }
                  </div>
                  <div className="mb-1"><b>State:</b>{" "}
                    {
                      customers.find(
                        (c) => String(c.id) === String(invoicePreviewData.customerId)
                      )?.state
                    }
                  </div>
                  <div className="mb-1"><b>GSTIN:</b>{" "}
                    {
                      customers.find(
                        (c) => String(c.id) === String(invoicePreviewData.customerId)
                      )?.gst_number
                    }
                  </div>
                </>
              )}
            </td>
          </tr>
        </tbody>
      </table>
      {/* ...rest unchanged for brevity */}
      {/* ...rest of InvoicePreviewContent is unchanged (omitted for brevity)... */}
      <table
        className="w-full border border-black border-collapse mt-[2px] mb-2 rounded-lg"
        style={{ fontSize: "11.5px", borderWidth: "1.2px" }}
      >
        <thead>
          <tr style={{ height: "22px", fontSize: "11.7px" }}>
            <th
              className="border border-black"
              style={{ padding: "3px 6px", textAlign: "center", fontWeight: 600 }}
            >
              Particulars
            </th>
            <th
              className="border border-black"
              style={{ padding: "3px 6px", textAlign: "center", fontWeight: 600, width: "90px" }}
            >
              HSN/SAC
            </th>
            <th
              className="border border-black"
              style={{ padding: "3px 6px", textAlign: "center", fontWeight: 600, width: "110px" }}
            >
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {invoicePreviewData.rows.map((r, i) => (
            <tr key={i}>
              <td
                className="border border-black align-top"
                style={{
                  padding: "7px 10px 24px 7px",
                  fontSize: "12.4px",
                  minHeight: "90px",
                }}
              >
                <div>
                  <div style={{ whiteSpace: "pre-line" }}>{r.particulars}</div>
                  {(i === 0) && (
                    <div style={{ marginTop: 24, fontWeight: 700, letterSpacing: 1, fontFamily: "inherit", fontSize: "12px", display: "flex", flexDirection: "column" }}>
                      {/* <span style={{ marginLeft: "auto", marginBottom: "-2px", lineHeight: 1.33, color: "#222", fontWeight: 600 }}>CGST</span>
                      <span style={{ marginLeft: "auto", lineHeight: 1.33, color: "#222", fontWeight: 600 }}>SGST</span> */}
                    </div>
                  )}
                </div>
              </td>
              <td
                className="border border-black align-top"
                style={{
                  padding: "7px 8px",
                  fontSize: "12.6px",
                  textAlign: "center",
                  minWidth: "64px",
                }}
              >
                {r.hsn_sac ? r.hsn_sac.split(' - ')[0] : ''}
              </td>
              <td
                className="border border-black align-top text-right"
                style={{
                  padding: "7px 8px",
                  fontSize: "12.6px",
                  fontFamily: "inherit",
                  minWidth: "84px",
                }}
              >
                {(() => {
                  const rawAmount = parseFloat(r.amount) || 0;
                  const gstRateNum = Number(invoicePreviewData.selectedGstRate) || 0;
                  const displayValue = invoicePreviewData.gstIncluded ? (rawAmount / (1 + gstRateNum / 100)) : rawAmount;
                  return Math.round(displayValue).toLocaleString("en-IN");
                })()}
              </td>
            </tr>
          ))}
          {/* CGST/SGST Rows visually aligned next to Particulars, but as separate cells for Amount column */}
          <tr>
            {/* Empty cells for alignment under Particulars and HSN/SAC */}
            <td className="border-t-0 border-l border-b-0 border-black text-end" style={{ padding: 0 }}>
              <span style={{ marginLeft: "auto", marginBottom: "-2px", lineHeight: 1.33, color: "#222", fontWeight: 600 }}>
                CGST ({Number(invoicePreviewData.selectedGstRate)/2}%)
              </span>
            </td>
            <td className="border-t-0 border-b-0 border-black" style={{ padding: 0 }}></td>
            <td className="border border-black text-right" style={{ padding: "5.3px 8px", fontSize: "12.7px", fontWeight: 500 }}>
              {Number(invoicePreviewData.cgstAmount).toLocaleString("en-IN")}
            </td>
          </tr>
          <tr>
            <td className="border-t-0 border-l border-b-0 border-black text-end" style={{ padding: 0 }}> <span style={{ marginLeft: "auto", marginBottom: "-2px", lineHeight: 1.33, color: "#222", fontWeight: 600 }}>SGST({Number(invoicePreviewData.selectedGstRate)/2}%)</span></td>
            <td className="border-t-0 border-b-0 border-black" style={{ padding: 0 }}></td>
            <td className="border border-black text-right" style={{ padding: "5.3px 8px", fontSize: "12.7px", fontWeight: 500 }}>
              {Number(invoicePreviewData.sgstAmount).toLocaleString("en-IN")}
            </td>
          </tr>
          {Number(invoicePreviewData.igstAmount) > 0 && (
            <tr>
              {/* If IGST needs to be shown */}
              <td className="border p-2 text-right font-semibold" colSpan={2}>IGST</td>
              <td className="border p-2 text-right">
                ₹ {invoicePreviewData.igstAmount}
              </td>
            </tr>
          )}
          <tr className="font-bold bg-green-50 invoice-total">
            <td className="border p-2 text-right" colSpan={2}>Total</td>
            <td className="border p-2 text-right text-green-700 text-[16px] font-bold">
              ₹ {invoicePreviewData.totalAmount}
            </td>
          </tr>
        </tbody>
      </table>
      {/* ... more content ... */}
      <div className="mt-2 mb-1">
        {/* Amount in Words */}
        <div className="w-full border border-gray-200 rounded-lg bg-gray-50 px-2 py-2 mb-2 flex flex-col">
          <span className="text-lg font-bold text-blue-900 mb-1" style={{ fontSize: '12.5px' }}>Amount Chargeable (in words)</span>
          <span className="text-[11.5px] text-gray-700 font-mono">{amountInWords} /-</span>
        </div>
        {/* ...rest unchanged ... */}
        {/* Tax Details Table */}
        <div className="w-full border border-blue-200 rounded-xl bg-white px-0 sm:px-1 py-2 mb-2 shadow-lg">
          <div className="flex items-center border-b border-blue-100 pb-1 px-2">
            <span className="text-blue-900 text-lg font-semibold tracking-wide" style={{ fontSize: '12.2px' }}>Tax Details</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse mt-1">
              <thead>
                <tr className="bg-blue-50 text-blue-900 text-[11px]">
                  <th className="border border-blue-200 px-2 py-1 text-left font-semibold align-middle">HSN/SAC</th>
                  <th className="border border-blue-200 px-2 py-1 text-right font-semibold align-middle">Taxable Value (₹)</th>
                  <th className="border border-blue-200 px-1 py-0" colSpan={2}>
                    <div className="flex flex-col text-center">
                      <span className="font-semibold" style={{ fontSize: "11px" }}>IGST</span>
                      <div className="flex justify-between w-full border-t border-blue-100 mt-1">
                        <span className="flex-1 border-r border-blue-100 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>Rate (%)</span>
                        <span className="flex-1 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>Amount (₹)</span>
                      </div>
                    </div>
                  </th>
                  <th className="border border-blue-200 px-1 py-0" colSpan={2}>
                    <div className="flex flex-col text-center">
                      <span className="font-semibold" style={{ fontSize: "11px" }}>SGST</span>
                      <div className="flex justify-between  w-full border-t border-blue-100 mt-1">
                        <span className="flex-1 border-r border-blue-100 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>Rate (%)</span>
                        <span className="flex-1 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>Amount (₹)</span>
                      </div>
                    </div>
                  </th>
                  <th className="border border-blue-200 px-1 py-0" colSpan={2}>
                    <div className="flex flex-col text-center">
                      <span className="font-semibold" style={{ fontSize: "11px" }}>CGST</span>
                      <div className="flex justify-between w-full border-t border-blue-100 mt-1">
                        <span className="flex-1 border-r border-blue-100 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>Rate (%)</span>
                        <span className="flex-1 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>Amount (₹)</span>
                      </div>
                    </div>
                  </th>
                  <th className="border border-blue-200 px-2 py-1 text-right font-semibold align-middle">Total Tax<br />Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const { taxRows } = taxTableData;
                  if (!taxRows.length) {
                    // Demo Row
                    return (
                      <tr className="even:bg-blue-50 text-[11px]">
                        <td className="border border-blue-100 px-2 py-1 align-middle"></td>
                        <td className="border border-blue-100 px-2 py-1 text-right align-middle"></td>
                        <td className="border border-blue-100 px-1 py-1 text-center align-middle"></td>
                        <td className="border border-blue-100 px-1 py-1 text-right align-middle"></td>
                        <td className="border border-blue-100 px-1 py-1 text-center align-middle"></td>
                        <td className="border border-blue-100 px-1 py-1 text-right align-middle"></td>
                        <td className="border border-blue-100 px-1 py-1 text-center align-middle"></td>
                        <td className="border border-blue-100 px-1 py-1 text-right align-middle"></td>
                        <td className="border border-blue-100 px-2 py-1 text-right align-middle font-semibold"></td>
                      </tr>
                    );
                  }
                  return taxRows.map((row, idx) => (
                    <tr key={row.hsn + idx} className="even:bg-blue-50 text-[11px]">
                      {/* HSN */}
                      <td className="border border-blue-100 px-2 py-1 align-middle">{row.hsn}</td>
                      {/* Taxable Value */}
                      <td className="border border-blue-100 px-2 py-1 text-right align-middle">
                        {row.taxableValue}
                      </td>
                      {/* IGST Rate and Amount */}
                      <td className="border border-blue-100 px-1 py-1 text-center align-middle">
                        {row.igstRate ? `${row.igstRate}%` : "-"}
                      </td>
                      <td className="border border-blue-100 px-1 py-1 text-right align-middle">
                        {row.igstAmount ? row.igstAmount : "-"}
                      </td>
                      {/* SGST/UTGST Rate and Amount */}
                      <td className="border border-blue-100 px-1 py-1 text-center align-middle">
                        {row.sgstRate ? `${row.sgstRate}%` : "-"}
                      </td>
                      <td className="border border-blue-100 px-1 py-1 text-right align-middle">
                        {row.sgstAmount ? row.sgstAmount : "-"}
                      </td>
                      <td className="border border-blue-100 px-1 py-1 text-center align-middle">
                        {row.sgstRate ? `${row.sgstRate}%` : "-"}
                      </td>
                      <td className="border border-blue-100 px-1 py-1 text-right align-middle">
                        {row.sgstAmount ? row.sgstAmount : "-"}
                      </td>
                      {/* Total Tax */}
                      <td className="border border-blue-100 px-2 py-1 text-right align-middle font-semibold">
                        {row.totalTax}
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
        {/* Tax Amount (in words) - dynamic conversion */}
        <div className="w-full border border-gray-200 rounded-lg bg-gray-50 px-2 py-2 my-2 flex flex-col">
          <span className="text-lg font-bold text-blue-900 mb-1" style={{ fontSize: '12.5px' }}>Tax Amount (in words)</span>
          <span className="text-[11.5px] text-gray-700 font-mono">{taxAmountInWords} /-</span>
        </div>
      </div>
      {/* End Custom tax table and amount in words */}

      {/* BANK DETAILS ... unchanged ... */}
      <div className="w-full border border-blue-200 border-collapse mt-[4px] rounded-lg overflow-hidden flex flex-row items-stretch bg-white">
        <div className="w-1/2 border-r border-blue-200 p-2 flex flex-col justify-between bg-gray-50" style={{ fontSize: '11px' }}>
         {/* REMARKS SECTION */}
         {invoicePreviewData.remarks && (
                <div className="mb-1 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md text-gray-800">
                  <b className="text-blue-900">Remarks:</b>
                  <div className="mt-1" style={{ whiteSpace: 'pre-line' }}>
                    {invoicePreviewData.remarks}
                  </div>
                </div>
              )}
              {/* END REMARKS */}
          <div>
            <b className="text-blue-900">Company's PAN: </b>
            <span className="font-semibold text-gray-800">{
              companies.find((c) => String(c.id) === String(invoicePreviewData.companyId))?.pancard || 'N/A'
            }</span>
          </div>
          <div className="mt-2">
            <b className="text-blue-900 mb-1 block">Declaration</b>
            <div className="text-gray-800 mt-1">
              {invoicePreviewData.invoice_type === 'lut'
                ? 'We declare that this invoice shows the actual price of the goods/services described. Supply is meant for export under Bond or Letter of Undertaking without payment of IGST as per Section 16(3) of IGST Act, 2017.'
                : invoicePreviewData.invoice_type === 'commission'
                ? 'We declare that this invoice is raised towards commission on the transaction detailed above. All particulars are true and correct.'
                : 'We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.'}
            </div>
          </div>
        </div>
        <div className="w-1/2 p-2 flex flex-row justify-between text-left bg-white">
          <div className="flex flex-col w-full border border-blue-200 border-collapse mt-1 rounded-lg overflow-hidden">
            <div className="border p-2 w-full align-top bg-gray-50 rounded-bl-lg flex flex-col" style={{ fontSize: '11px' }}>
              <b className="text-blue-900">Bank Details</b>
              {(() => {
                const bank = invoicePreviewData.bankDetails;
                if (bank) {
                  return (<>
                    <div className="mt-1 mb-1">A/c Holder Name :{" "}{bank.account_holder_name || companies.find((c) => c.id == invoicePreviewData.companyId)?.company_name}</div>
                    <div>Bank Name : <span className="font-semibold">{bank.bank_name}</span></div>
                    <div>A/c No : <span className="font-semibold">{bank.account_number}</span></div>
                    <div>Branch : <span className="font-semibold">{bank.branch || 'N/A'}</span></div>
                    {bank.ifsc_code && <div>IFSC : <span className="font-semibold">{bank.ifsc_code}</span></div>}
                  </>);
                }
                return (<>
                  <div className="mt-1 mb-1">A/c Holder Name :{" "}{companies.find((c) => c.id == invoicePreviewData.companyId)?.company_name}</div>
                  <div className="text-gray-400 italic">No bank details on file</div>
                </>);
              })()}
            </div>
            <div className="border border-l-0 p-2 w-full text-right align-top bg-white rounded-br-lg flex flex-col justify-between">
              <div>
                <div className="mb-2">For <b>
                  {
                    companies.find((c) => c.id == invoicePreviewData.companyId)?.company_name
                  }
                </b></div>
                {/* Signature image for company */}
                {(() => {
                  const companyObj = companies.find(
                    (c) => String(c.id) === String(invoicePreviewData.companyId)
                  );
                  const signatureUrl = companyObj && companySignatureUrls[String(companyObj.id)];
                  if (
                    companyObj &&
                    typeof companyObj.signature === "string" &&
                    companyObj.signature.trim() !== "" &&
                    signatureUrl
                  ) {
                    return (
                      <img
                        src={signatureUrl}
                        alt="Authorised Signature"
                        className="h-12 w-auto object-contain ml-auto"
                        style={{ maxWidth: "120px" }}
                        onError={e => { e.currentTarget.style.display = "none"; }}
                      />
                    );
                  }
                  return (
                    <div className="text-gray-400 italic text-sm text-center" style={{ fontSize: '10px' }}>
                      No signature on file
                    </div>
                  );
                })()}
                <div className="pt-1 font-semibold" style={{ fontSize: '10.5px' }}>Authorised Signatory</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SuccessActionsPopup: React.FC<{
  open: boolean;
  onClose: () => void;
  goToInvoices: () => void;
  handleAddMoreInvoices: () => void;
  viewInvoice: () => void;
  downloadInvoice: () => void;
  shareInvoice: () => void;
  invoicePreviewData: any;
}> = ({
  open,
  onClose,
  goToInvoices,
  handleAddMoreInvoices,
  viewInvoice,
  downloadInvoice,
  shareInvoice,
  invoicePreviewData,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.49)" }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative flex flex-col items-center animate-fadeIn">
        <button
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-900 text-xl font-bold"
          style={{ lineHeight: "1" }}
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
        <div className="flex items-center justify-center mb-1">
          <div className="animate-bounce text-green-600" style={{ fontSize: 60, marginBottom: 8 }}>
            <svg style={{ height: 60, width: 60 }} fill="none" viewBox="0 0 48 48" stroke="currentColor">
              <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="3.2" fill="#ECFDF5"/>
              <path stroke="#22C55E" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" d="M14.5 25l7 7 13-14"/>
            </svg>
          </div>
        </div>
        <div className="text-2xl font-bold text-green-700 mb-2 mt-1 text-center">
          Invoice Created Successfully!
        </div>
        <div className="text-md text-gray-700 mb-6 text-center max-w-lg">
          Your invoice has been generated and saved.<br className="hidden sm:block" />Choose what you want to do next:
        </div>
        <div className="flex flex-wrap gap-4 justify-center mb-3">
          <button
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow transition"
            onClick={() => { onClose(); goToInvoices(); }}
          >
            Go to Invoices
          </button>
          <button
            className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl shadow transition"
            onClick={() => { onClose(); handleAddMoreInvoices(); }}
          >
            Add More Invoices
          </button>
        </div>
        <div className="flex flex-wrap gap-4 justify-center">
          <button
            className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-bold rounded-xl shadow transition"
            onClick={() => { onClose(); viewInvoice(); }}
            disabled={!invoicePreviewData}
          >
            View Invoice
          </button>
          <button
            className="px-6 py-2 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl shadow transition"
            onClick={() => { onClose(); downloadInvoice(); }}
            disabled={!invoicePreviewData}
          >
            Download Invoice
          </button>
          <button
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow transition"
            onClick={() => { onClose(); shareInvoice(); }}
            disabled={!invoicePreviewData}
          >
            Share Invoice
          </button>
        </div>
      </div>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(.95);}
            to { opacity: 1; transform: scale(1);}
          }
          .animate-fadeIn {
            animation: fadeIn 0.18s cubic-bezier(.42,0,.58,1.0);
          }
        `}
      </style>
    </div>
  );
};


function decodeToken(token: any) {
  if (!token) return {};
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
}

const InvoiceCreator: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [settings, setSettings] = useState<Setting | null>(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [companyId, setCompanyId] = useState<string | number>("");
  const [customerId, setCustomerId] = useState<string | number>("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [rows, setRows] = useState<InvoiceRow[]>([{ ...initialInvoiceRow }]);
  const [sgstAmount, setSgstAmount] = useState<number>(0);
  const [cgstAmount, setCgstAmount] = useState<number>(0);
  const [igstAmount, setIgstAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [selectedGstRate, setSelectedGstRate] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [invoiceTotalCount, setInvoiceTotalCount] = useState<number>(0);
  const [composedInvoiceNo, setComposedInvoiceNo] = useState<string>("");
  const [invoicePreviewData, setInvoicePreviewData] = useState<{
    companyId: string | number;
    customerId: string | number;
    invoiceNo: string;
    invoiceDate: string;
    rows: InvoiceRow[];
    sgstAmount: number;
    cgstAmount: number;
    igstAmount: number;
    totalAmount: number;
    selectedGstRate: string;
    remarks?: string;
    composedInvoiceNo?: string;
    bankDetails?: BankDetail | null;
    shipToAddress?: string;
    invoice_type?: string;
    gstIncluded?: boolean;
    lut_number?: string;
    commission_rate?: string;
    commission_base_amount?: string;
  } | null>(null);
  const [addMonthInInvoiceNo, setAddMonthInInvoiceNo] = useState<boolean>(true);
  const [remarks, setRemarks] = useState<string>("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [companySignatureUrls, setCompanySignatureUrls] = useState<{ [key: string]: string }>({});
  const [gstIncluded, setGstIncluded] = useState<boolean>(false);
  const [bankDetails, setBankDetails] = useState<BankDetail | null>(null);
  const [shipToAddress, setShipToAddress] = useState<string>("");
  const [invoiceType, setInvoiceType] = useState<string>("tax");
  const [discountPercent, setDiscountPercent] = useState<string>("0");
  const [lutNumber, setLutNumber] = useState<string>("");
  const [commissionRate, setCommissionRate] = useState<string>("");
  const [commissionBaseAmount, setCommissionBaseAmount] = useState<string>("");
  const [serviceTypes, setServiceTypes] = useState<any[]>([]);

  useEffect(() => {
    axios.get(`${API_URL}/auth/service-types`)
      .then((res) => {
        setServiceTypes(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("Failed to load service types:", err);
      });
  }, []);

  // ---- BEGIN: Track role logic -----
  const [userRole, setUserRole] = useState<string | null>(null);
  const [myCompanyId, setMyCompanyId] = useState<string | number | null>(null);
  const [decodedUserId, setDecodedUserId] = useState<string | number | null>(null);

  useEffect(() => {
    async function fetchUserData() {
      try {
        let userData;
        const token = localStorage.getItem("token");
        let decoded: any = null;
        if (token) {
          decoded = decodeToken(token);
        }
        const role = decoded.role;
        setUserRole(role || null);
        setDecodedUserId(
          decoded?.userId ||
          decoded?.id ||
          decoded?.userid ||
          decoded?.uid ||
          decoded?.user_id ||
          decoded?.company_id ||
          decoded?.companyId ||
          decoded?.cid
        );
        let companyIdForCompany: string | number | null = null;

        if (decoded && role === "company") {
          const res = await axios.get(`${API_URL}/auth/companies`);
          const companyId =
            decoded.userId ||
            decoded.id ||
            decoded.companyId ||
            decoded.company_id ||
            decoded.cid;
          let company =
            (Array.isArray(res.data)
              ? res.data
              : []
            ).find((c: any) =>
              String(c.id) === String(companyId)
            );
          if (company) {
            userData = {
              ...company,
              id: company.id ?? company.company_id,
              name: company.company_name || company.name || "Company",
              profile_img: company.profile_img || "",
              email: company.email || "",
              mobile: company.mobile || company.phone || "",
            };
            setMyCompanyId(company.id ?? company.company_id);
            setCompanyId(company.id ?? company.company_id);
          }
        } else if (decoded) {
          const res = await axios.get(`${API_URL}/auth/allusersinfo`);
          let userId =
            decoded.userId ||
            decoded.id ||
            decoded.userid ||
            decoded.uid ||
            decoded.user_id;
          let userFound =
            (Array.isArray(res.data)
              ? res.data
              : []
            ).find((u: any) => String(u.id) === String(userId));
          if (userFound) {
            userData = {
              ...userFound,
              name: userFound.username || "User",
              profile_img: userFound.profile_img || "",
              email: userFound.email || "",
              mobile: userFound.mobile || "",
            };
          }
        }
      } catch (error) {
      }
    }
    fetchUserData();
  }, []);
  // ---- END: Track role logic -----

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/auth/companies`)
      .then((res) => {
        setCompanies(res.data);

        const companiesList: Company[] = res.data;
        const fetchSignatures = async () => {
          const urlMap: { [key: string]: string } = {};
          await Promise.all(
            companiesList.map(async (company) => {
              if (company.signature) {
                try {
                  if (
                    company.signature.startsWith("http://") ||
                    company.signature.startsWith("https://") ||
                    company.signature.startsWith("//")
                  ) {
                    urlMap[String(company.id)] = company.signature;
                  } else {
                    urlMap[String(company.id)] = `${API_URL}${company.signature}`;
                  }
                } catch {
                }
              }
            })
          );
          setCompanySignatureUrls(urlMap);
        };
        fetchSignatures();
      })
      .catch(() => setErrorMsg("Could not fetch companies."))
      .finally(() => setLoading(false));
  }, []);

  // Modified: store original customers and filteredCustomers separately
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);

  // Fetch all customers once (and always get createdBy)
  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/auth/customers`)
      .then((res) => {
        setAllCustomers(res.data);
      })
      .catch(() => setErrorMsg("Could not fetch buyers/customers."))
      .finally(() => setLoading(false));
  }, []);

  // Filtering logic for buyers according to role/company
  useEffect(() => {
    // Only apply after userRole and decodedUserId and companies loaded
    if (
      userRole !== null &&
      decodedUserId !== null &&
      allCustomers.length > 0 &&
      companies.length > 0
    ) {
      // If role is company, see if companyId === decodedUserId
      // If so, show only buyers created by that company/user.
      // If not, show all.
      let buyers: Customer[] = [];
      if (
        userRole === "company" &&
        String(companyId || myCompanyId) === String(decodedUserId)
      ) {
        buyers = allCustomers.filter(
          (c: any) =>
            String(c.company_id) === String(decodedUserId) ||
            String(c.company_id) === String(decodedUserId)
        );
      } else {
        buyers = [...allCustomers];
      }
      setFilteredCustomers(buyers);
      setCustomers(buyers);  // for legacy compatibility
    } else if (allCustomers.length > 0) {
      setFilteredCustomers([...allCustomers]);
      setCustomers([...allCustomers]);
    }
  }, [userRole, decodedUserId, companies, allCustomers, companyId, myCompanyId]); // run when companies, role, user, allCustomers, companyId change

  useEffect(() => {
    axios.get(`${API_URL}/auth/setting`)
      .then((res) => setSettings(res.data))
      .catch(() => setErrorMsg("Could not fetch tax settings."));
  }, []);
  const selectedCompany = companies.find((c) => String(c.id) === String(companyId));
  const selectedCustomer = customers.find((c) => String(c.id) === String(customerId));
  const gstSlabOptions = selectedCompany ? parseGSTSLAB(selectedCompany.GST_SLAB) : [];

  // ...calculation and other logic as before...

  const calculateTaxesAndTotal = useCallback(
    (
      _rows = rows,
      _companyId = companyId,
      _customerId = customerId,
      _selectedGstRate = selectedGstRate,
      _gstIncluded = gstIncluded
    ) => {
      if (
        !_companyId ||
        !_customerId ||
        companies.length === 0 ||
        customers.length === 0
      ) {
        setSgstAmount(0);
        setCgstAmount(0);
        setIgstAmount(0);
        setTotalAmount(0);
        return;
      }
      const company = companies.find((c) => String(c.id) === String(_companyId));
      const customer = customers.find((c) => String(c.id) === String(_customerId));
      if (!company || !customer) {
        setSgstAmount(0);
        setCgstAmount(0);
        setIgstAmount(0);
        setTotalAmount(0);
        return;
      }
      let sum = _rows.reduce((a, b) => a + (parseFloat(b.amount) || 0), 0);
      const discountVal = parseFloat(discountPercent) || 0;
      if (discountVal > 0) {
        sum = sum - (sum * discountVal / 100);
      }

      let gstRate = 0;
      // LUT Invoices bypass GST (treated as 0%)
      if (invoiceType === "lut") {
        gstRate = 0;
      } else if (_selectedGstRate && !isNaN(Number(_selectedGstRate))) {
        gstRate = Number(_selectedGstRate);
      } else if (settings) {
        gstRate = 0;
      } else {
        gstRate = 0;
      }
      
      let totalBase = sum;
      if (_gstIncluded && gstRate > 0) {
        totalBase = sum / (1 + gstRate / 100);
      }
 
      let _sgst = 0,
        _cgst = 0,
        _igst = 0;
      if (
        company.state &&
        customer.state &&
        company.state.trim().toLowerCase() ===
        customer.state.trim().toLowerCase()
      ) {
        _sgst = Math.round(((gstRate / 2) * totalBase) / 100);
        _cgst = Math.round(((gstRate / 2) * totalBase) / 100);
        _igst = 0;
        if (_gstIncluded && gstRate > 0) {
          // Adjust to match exact totals
          const totalTax = Math.round(totalBase * gstRate / 100);
          _sgst = Math.round(totalTax / 2);
          _cgst = totalTax - _sgst;
        }
      } else {
        _sgst = 0;
        _cgst = 0;
        _igst = Math.round((gstRate * totalBase) / 100);
      }
      setSgstAmount(_sgst);
      setCgstAmount(_cgst);
      setIgstAmount(_igst);
      // When GST is included, total amount remains the exact user-entered sum
      setTotalAmount(_gstIncluded && gstRate > 0 ? Math.round(sum) : Math.round(totalBase + _sgst + _cgst + _igst));
    },
    [companies, customers, settings, companyId, customerId, rows, selectedGstRate, gstIncluded, discountPercent, invoiceType]
  );

  useEffect(() => {
    if (companyId && invoiceDate) {
      const fy = getFinancialYear(invoiceDate);
      axios.get(`${API_URL}/auth/invoices`)
        .then((res) => {
          const invoices = Array.isArray(res.data) ? res.data : [];
          const fyStartYear = Number(`20${fy.substring(0, 2)}`);
          const fyEndYear = Number(`20${fy.substring(3, 5)}`);
          const fyStart = new Date(fyStartYear, 3, 1).getTime();
          const fyEnd = new Date(fyEndYear, 2, 31, 23, 59, 59, 999).getTime();
          const filtered = invoices.filter(inv =>
            String(inv.Company_id) === String(companyId) &&
            (() => {
              const dt = new Date(inv.Date).getTime();
              return dt >= fyStart && dt <= fyEnd;
            })()
          );
          
          let nextSeq = filtered.length;
          if (filtered.length > 0) {
            let maxSeq = 0;
            filtered.forEach(inv => {
              const matches = inv.InvoiceNo ? inv.InvoiceNo.match(/(\d+)(?!.*\d)/) : null;
              if (matches) {
                const seq = parseInt(matches[1], 10);
                if (seq > maxSeq) {
                  maxSeq = seq;
                }
              }
            });
            if (maxSeq > 0) {
              nextSeq = maxSeq;
            }
          }
          setInvoiceTotalCount(nextSeq);
        })
        .catch(() => setInvoiceTotalCount(0));
    } else {
      setInvoiceTotalCount(0);
    }
  }, [companyId, invoiceDate]);

  const [invoiceNoAuto, setInvoiceNoAuto] = useState<boolean>(true);
  const getDefaultInvoiceNo = () => {
    if (selectedCompany && invoiceDate && invoiceTotalCount !== null) {
      return padNumber(invoiceTotalCount + 1, 3);
    }
    return "";
  };
  const getComposedInvoiceNo = (): string => {
    return computeFinalInvoiceNo({
      company: selectedCompany,
      invoiceNo: invoiceNo,
      invoiceDate,
      invoiceTotalCount,
      addMonthInInvoiceNo,
      separator: "-",
      settings,
    });
  };
  useEffect(() => {
    setComposedInvoiceNo(getComposedInvoiceNo());
  }, [selectedCompany, invoiceNo, invoiceDate, invoiceTotalCount, addMonthInInvoiceNo, settings]);

  useEffect(() => {
    if (invoiceNoAuto) {
      setInvoiceNo(getDefaultInvoiceNo());
    }
    // eslint-disable-next-line
  }, [selectedCompany, invoiceDate, invoiceTotalCount, addMonthInInvoiceNo]);

  const handleInvoiceNoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInvoiceNo(e.target.value);
    setInvoiceNoAuto(false);
  };

  useEffect(() => {
    setSelectedGstRate("");
    setInvoiceDate("");
    setInvoiceNo("");
    setInvoiceNoAuto(true);
    setInvoiceTotalCount(0);
    setBankDetails(null);
    setShipToAddress("");
  }, [companyId]);

  // Fetch bank details when companyId changes
  useEffect(() => {
    if (companyId) {
      axios.get(`${API_URL}/auth/company/${companyId}/bank`)
        .then((res) => {
          const banks = Array.isArray(res.data) ? res.data : [];
          const defaultBank = banks.find((b: any) => b.is_default === 1 || b.is_default === '1' || b.is_default === true);
          setBankDetails(defaultBank || (banks.length > 0 ? banks[0] : null));
        })
        .catch(() => setBankDetails(null));
    }
  }, [companyId]);

  useEffect(() => {
    calculateTaxesAndTotal();
  }, [rows, companyId, customerId, settings, companies, customers, selectedGstRate, gstIncluded]);

  const onRowChange = (i: number, name: string, value: string) => {
    const newRows = [...rows];
    (newRows[i] as any)[name] = value;
    setRows(newRows);
    setTimeout(() => {
      calculateTaxesAndTotal(newRows, companyId, customerId, selectedGstRate);
    }, 0);
  };

  const onCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCompanyId(e.target.value);
    setRows([{ ...initialInvoiceRow }]);
    setSelectedGstRate("");
    setInvoiceDate("");
    setInvoiceNo("");
    setInvoiceNoAuto(true);

    setTimeout(() => {
      calculateTaxesAndTotal([{ ...initialInvoiceRow }], e.target.value, customerId, "");
    }, 0);
  };
  const onCustomerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCustomerId(e.target.value);
    setTimeout(() => {
      calculateTaxesAndTotal(rows, companyId, e.target.value, selectedGstRate);
    }, 0);
  };
  const onGstRateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGstRate(e.target.value);
    setTimeout(() => {
      calculateTaxesAndTotal(rows, companyId, customerId, e.target.value);
    }, 0);
  };
  const onInvoiceDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInvoiceDate(e.target.value);
    if (invoiceNoAuto) setInvoiceNo(getDefaultInvoiceNo());
  };
  const handleAddMonthToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddMonthInInvoiceNo(e.target.checked);
    if (invoiceNoAuto) setInvoiceNo(getDefaultInvoiceNo());
  };

  const addRow = () => {
    const updatedRows = [...rows, { ...initialInvoiceRow }];
    setRows(updatedRows);
    setTimeout(() => {
      calculateTaxesAndTotal(updatedRows, companyId, customerId, selectedGstRate);
    }, 0);
  };
  const removeRow = (i: number) => {
    if (rows.length === 1) return;
    const rr = [...rows];
    rr.splice(i, 1);
    setRows(rr);
    setTimeout(() => {
      calculateTaxesAndTotal(rr, companyId, customerId, selectedGstRate);
    }, 0);
  };

  const handlePreview = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    setInvoicePreviewData({
      companyId,
      customerId,
      invoiceNo: composedInvoiceNo,
      invoiceDate,
      rows: JSON.parse(JSON.stringify(rows)),
      sgstAmount,
      cgstAmount,
      igstAmount,
      totalAmount,
      selectedGstRate,
      remarks: remarks.trim() ? remarks.trim() : undefined,
      composedInvoiceNo: composedInvoiceNo,
      bankDetails: bankDetails,
      shipToAddress: shipToAddress.trim() || undefined,
      gstIncluded,
    });
    setShowInvoice(true);
  };

  const goToInvoices = () => {
    window.location.href = "/invoices";
  };
  const handleAddMoreInvoices = () => {
    setSuccessMsg("");
    setShowSuccessPopup(false);
    setInvoicePreviewData(null);
    setShowInvoice(false);
    setCompanyId("");
    setCustomerId("");
    setInvoiceNo("");
    setInvoiceNoAuto(true);
    setInvoiceDate("");
    setSelectedGstRate("");
    setInvoiceTotalCount(0);
    setRows([{ ...initialInvoiceRow }]);
    setRemarks("");
    setSgstAmount(0);
    setCgstAmount(0);
    setIgstAmount(0);
    setTotalAmount(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const viewInvoice = () => {
    setShowInvoice(true);
    setShowSuccessPopup(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const downloadInvoice = () => {
    setShowInvoice(true);
    setTimeout(() => {
      handlePrint();
    }, 250);
    setShowSuccessPopup(false);
  };

  const shareInvoice = async () => {
    if (!invoicePreviewData) return;
    try {
      setShowInvoice(true);
      setShowSuccessPopup(false);

      await new Promise(resolve => setTimeout(resolve, 350));
      const elem = document.getElementById("invoice-print");
      if (!elem) return;
      if (navigator.share) {
        const html2canvasModule = await import("html2canvas");
        const html2canvas = html2canvasModule.default;
        html2canvas(elem).then(canvas => {
          canvas.toBlob(blob => {
            if (blob) {
              const file = new File([blob], `invoice-${invoicePreviewData.composedInvoiceNo || invoicePreviewData.invoiceNo}.png`, { type: "image/png" });
              navigator.share({
                title: "Invoice",
                text: "Sharing Invoice",
                files: [file]
              });
            }
          });
        });
      } else {
        alert("Share API not supported on your browser/device.");
      }
    } catch (err) {
      alert("Failed to share the invoice.");
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");
    setInvoicePreviewData({
      companyId,
      customerId,
      invoiceNo: composedInvoiceNo,
      invoiceDate,
      rows: JSON.parse(JSON.stringify(rows)),
      sgstAmount,
      cgstAmount,
      igstAmount,
      totalAmount,
      selectedGstRate,
      remarks: remarks.trim() ? remarks.trim() : undefined,
      composedInvoiceNo: composedInvoiceNo,
      bankDetails: bankDetails,
      shipToAddress: shipToAddress.trim() || undefined,
      invoice_type: invoiceType,
      lut_number: invoiceType === 'lut' ? lutNumber : undefined,
      commission_rate: invoiceType === 'commission' ? commissionRate : undefined,
      commission_base_amount: invoiceType === 'commission' ? commissionBaseAmount : undefined,
    });
    calculateTaxesAndTotal(rows, companyId, customerId, selectedGstRate);
    const someRowHasAmount =
      rows.length > 0 && rows.some((row) => parseFloat(row.amount) > 0);
    if (!companyId || !customerId || !invoiceNo || !invoiceDate || !someRowHasAmount) {
      setErrorMsg("Please fill all required fields and at least one row with amount.");
      return;
    }
    if (!selectedGstRate && invoiceType !== "lut") {
      setErrorMsg("Please select a GST Rate.");
      return;
    }
    if (invoiceType === "lut" && !lutNumber.trim()) {
      setErrorMsg("Please enter the LUT Number for LUT Invoice.");
      return;
    }
      // Check for duplicate invoice number before creating
      const checkRes = await axios.get(`${API_URL}/auth/invoices`);
      const existingInvoices = Array.isArray(checkRes.data) ? checkRes.data : [];
      const isDuplicate = existingInvoices.some((inv: any) => 
        String(inv.InvoiceNo).trim().toLowerCase() === composedInvoiceNo.trim().toLowerCase() &&
        Number(inv.delete) !== 1
      );
      if (isDuplicate) {
        const errorText = `An invoice with number ${composedInvoiceNo} already exists. Please choose a different invoice number sequence.`;
        window.alert(errorText);
        setErrorMsg(errorText);
        return;
      }

      setSubmitLoading(true);
      try {
        const particularsArr = rows.map((r) => r.particulars ?? "");
        const hsnArr = rows.map((r) => r.hsn_sac ?? "");
        const gstRateNum = Number(selectedGstRate);
        const amountArr = rows.map((r) => {
          const rawAmt = parseFloat(r.amount) || 0;
          const finalAmt = (gstIncluded && gstRateNum > 0) ? (rawAmt / (1 + gstRateNum / 100)) : rawAmt;
          return Math.round(finalAmt).toString();
        });
        const numericRows = rows.map((row) => ({
          ...row,
          amount: parseFloat(row.amount) || 0,
        }));
        let totalBase = numericRows.reduce((a, b) => a + b.amount, 0);
        
        if (gstIncluded && gstRateNum > 0) {
          totalBase = totalBase / (1 + gstRateNum / 100);
        }
        
        const SGSTAmount = (gstRateNum / 2 * totalBase) / 100;
        const CGSTAmount = (gstRateNum / 2 * totalBase) / 100;
        const IGSTAmount = (gstRateNum * totalBase) / 100;
        const company = companies.find((c) => String(c.id) === String(companyId));
        const customer = customers.find((c) => String(c.id) === String(customerId));
        const sameState =
          company &&
          customer &&
          company.state &&
          customer.state &&
          company.state.trim().toLowerCase() === customer.state.trim().toLowerCase();

        const finalInvNo = composedInvoiceNo;

        const payload: { [key: string]: any } = {
          Company_id: companyId,
          Customer_id: customerId,
          SGSTAmount: sameState ? Math.round(SGSTAmount) : 0,
          CGSTAmount: sameState ? Math.round(CGSTAmount) : 0,
          IGSTAmount: !sameState ? Math.round(IGSTAmount) : 0,
          InvoiceNo: finalInvNo,
          Date: invoiceDate,
          Particulars: JSON.stringify(particularsArr),
          HSN_SAC: JSON.stringify(hsnArr),
          Amounts: JSON.stringify(amountArr),
          Total_Amount: totalAmount,
          GST_RATE: selectedGstRate,
          invoice_type: invoiceType,
        };
      if (invoiceType === "lut" && lutNumber.trim()) {
        payload["lut_number"] = lutNumber.trim();
      }
      if (invoiceType === "commission") {
        if (commissionRate) payload["commission_rate"] = parseFloat(commissionRate) || 0;
        if (commissionBaseAmount) payload["commission_base_amount"] = parseFloat(commissionBaseAmount) || 0;
      }
      if (remarks.trim()) {
        payload["remarks"] = remarks.trim();
      }
      if (shipToAddress.trim()) {
        payload["shipToAddress"] = shipToAddress.trim();
      }

      const response = await axios.post(`${API_URL}/auth/invoice`, payload);
      const invoiceId = response.data.invoice_id;

      // PDF Generation and Upload with 10mm margins
      try {
        const elem = document.getElementById("invoice-print-hidden");
        if (elem) {
          const canvas = await html2canvas(elem, { scale: 2, useCORS: true });
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF("p", "mm", "a4");
          
          const margin = 10; // 10mm margins
          const imgWidth = 210 - (2 * margin);
          let imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          // Fit on a single page if height is too tall
          const maxPageHeight = 297 - (2 * margin);
          if (imgHeight > maxPageHeight) {
            imgHeight = maxPageHeight;
          }
          
          pdf.addImage(imgData, "PNG", margin, margin, imgWidth, imgHeight);

          const pdfBlob = pdf.output("blob");
          const formData = new FormData();
          formData.append("pdf", pdfBlob, `invoice-${finalInvNo}.pdf`);

          await axios.post(`${API_URL}/auth/invoice/${invoiceId}/upload-pdf`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
        }
      } catch (pdfErr) {
        console.error("Failed to auto-upload invoice PDF:", pdfErr);
      }

      setSuccessMsg("");
      setShowSuccessPopup(true);
      setRows([{ ...initialInvoiceRow }]);
      setInvoiceDate("");
      setCompanyId("");
      setCustomerId("");
      setSgstAmount(0);
      setCgstAmount(0);
      setIgstAmount(0);
      setTotalAmount(0);
      setSelectedGstRate("");
      setInvoiceNo("");
      setInvoiceNoAuto(true);
      setInvoiceTotalCount(0);
    } catch (err: any) {
      setErrorMsg(
        err?.response?.data?.error || "Failed to generate the invoice."
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const handlePrint = () => {
    const invoicePrintElem = document.getElementById("invoice-print");
    if (!invoicePrintElem) return;
    const printContents = invoicePrintElem.innerHTML;
    const printWindow = window.open("", "", "width=900,height=700");
    if (printWindow) {
      printWindow.document.write(`
        <html>
        <head>
          <title>Invoice</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 4px 7px !important; background: #f7fafb; color: #222; font-size: 11.5px !important; margin: 0; line-height: 1.25; max-width: 830px; width: 100vw; box-sizing: border-box; }
            .text-xs { font-size: 10px !important; }
            .font-semibold { font-weight: 600; }
            .font-bold { font-weight: 700; }
            .font-extrabold { font-weight: 800; }
            .text-lg { font-size: 14px !important; }
            .text-xl { font-size: 15px !important; }
            .text-green-700 { color: #15803d; }
            .text-blue-900 { color: #1e3a8a;}
            .text-blue-800 { color: #1e40af;}
            .text-blue-700 { color: #1d4ed8;}
            .text-gray-700 { color: #374151;}
            .text-gray-800 { color: #1f2937;}
            .italic { font-style: italic; }
            .bg-blue-50 { background: #eff6ff; }
            .bg-blue-100 { background: #dbeafe; }
            .bg-gradient-to-tr {
              background: linear-gradient(135deg, #fff 0%, #eaeffe 40%, #f0f7ff 100%);
            }
            .bg-gradient-to-t { background: linear-gradient(0deg, #eaeffe 0%, #e8f5fe 100%);}
            .bg-gradient-to-l { background: linear-gradient(270deg, #ffe4ef 0%, #fff 100%);}
            .bg-gray-50 { background: #f9fafb;}
            .bg-white { background: #fff;}
            .mb-1 { margin-bottom: 2px !important;}
            .ml-8 { margin-left: 16px !important;}
            .mb-2 { margin-bottom: 6px !important;}
            .mb-4 { margin-bottom: 10px !important;}
            .mb-5 { margin-bottom: 11px !important;}
            .mb-6 { margin-bottom: 13px !important;}
            .mt-1 { margin-top: 2px !important;}
            .mt-2 { margin-top: 4px !important;}
            .mt-4 { margin-top: 9px !important;}
            .mt-5 { margin-top: 10px !important;}
            .rounded-2xl { border-radius: 0.65rem;}
            .rounded-xl { border-radius: 0.4rem;}
            .rounded-lg { border-radius: 0.25rem;}
            .rounded-bl-lg { border-bottom-left-radius: 0.25rem;}
            .rounded-br-lg { border-bottom-right-radius: 0.25rem;}
            .border { border: 1px solid #cbd5e1;}
            .border-blue-100 { border-color: #dbeafe;}
            .border-blue-200 { border-color: #bfdbfe;}
            .border-blue-300 { border-color: #93c5fd;}
            .border-gray-200 { border-color: #e5e7eb;}
            .shadow, .shadow-lg, .shadow-2xl, .shadow-xl { box-shadow: none !important;}
            .table-title { background: #2062b8; color: #fff; font-weight: 600;}
            .invoice-total { background: #e9fbe8 !important; font-size: 13px !important;}
            .w-full { width: 100%;}
            .w-1\\/2 { width: 50%; }
            .align-top { vertical-align: top;}
            .text-right { text-align: right;}
            .text-center { text-align: center;}
            .flex { display: flex;}
            .flex-col { flex-direction: column;}
            .flex-row { flex-direction: row;}
            .items-center { align-items: center;}
            .items-end { align-items: flex-end;}
            .text-end { text-align: end;}
            .justify-between { justify-content: space-between;}
            .justify-end { justify-content: flex-end;}
            .justify-center { justify-content: center;}
            .gap-5 { gap: 10px;}
            .gap-6 { gap: 15px;}
            .overflow-auto { overflow: auto;}
            .overflow-x-auto { overflow-x: auto;}
            .h-12 { height: 36px !important;}
            .p-2 {padding:5px!important;} 
            .p-3 { padding: 7px !important;}
            .p-4 { padding: 7px !important;}
            .p-6 { padding: 14px !important;}
            .py-1 { padding-top: 2px !important; padding-bottom: 2px !important;}
            .py-2 { padding-top: 5px !important; padding-bottom: 5px !important;}
            .py-3 { padding-top: 7px !important; padding-bottom: 7px !important;}
            .px-2 { padding-left: 5px !important; padding-right: 5px !important;}
            .px-3 { padding-left: 8px !important; padding-right: 8px !important;}
            .px-6 { padding-left: 14px !important; padding-right: 14px !important;}
            .border-collapse { border-collapse: collapse;}
            .border-l-0 { border-left: 0;}
            .border-r { border-right: 1px solid #bfdbfe;}
             #invoice-print { min-height: 100%; overflow: visible; }
            /* Added styling for Tax Invoice heading */
            .tax-invoice-heading {
              text-align: center;
              font-size: 22px;
              font-weight: 800;
              letter-spacing: 1px;
              color: #1e3a8a;
              padding-top: 24px;
              padding-bottom: 18px;
            }
            @media print {
              html, body {
                width: 100vw;
                max-width: 856px !important;
                margin: 0 auto !important;
                padding: 0 !important;
                overflow: visible !important;
                font-size: 11.5px !important;
              }
              #invoice-print {
                padding: 0 6px !important;
                margin: 0 !important;
                width: 100% !important;
                max-width: 848px !important;
                max-height: none !important;
                overflow: visible !important;
              }
              .shadow, .shadow-lg, .shadow-xl, .shadow-2xl { box-shadow: none !important;}
              .bg-gradient-to-tr, .bg-gradient-to-t, .bg-gradient-to-l, .bg-gray-50, .bg-blue-50, .bg-blue-100, .bg-white { background: #fff !important; }
              tr, td, th, table, div, span, section {
                page-break-inside: avoid !important;
                page-break-after: auto !important;
                page-break-before: auto !important;
              }
              table, tbody, thead, tfoot, tr, td, th {
                margin: 0 !important;
                padding: 0 !important;
              }
              .tax-invoice-heading {
                text-align: center;
                font-size: 22px;
                font-weight: 800;
                letter-spacing: 1px;
                color: #1e3a8a;
                padding-top: 24px;
                padding-bottom: 18px;
              }
            }
          </style>
        </head>
        <body>
          ${printContents.replace(
            /^(\s*)<div[^>]*id="?invoice-print"?[^>]*>/i,
            ""
          ).replace(/<\/div>\s*$/, "")}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 350);
      };
    }
  };

  const companyHSNOptions = selectedCompany ? parseHSNSAC(selectedCompany.HSN_SAC) : [];

  // --- Render (main return) ---
  // Find out, in render, if the user is company role, and if so which id
  const isCompanyRole = userRole === "company" && myCompanyId !== null && myCompanyId !== undefined && !!companies.length;

  // ...tax-table helper as before...

  function getTaxTableData() {
    if (!invoicePreviewData) return {
      taxRows: [],
      totalTax: 0,
      uniqueHSN: []
    };
    const uniqueHSN: string[] = [
      ...new Set(invoicePreviewData.rows.map(r => r.hsn_sac).filter(Boolean))
    ];
    const result = uniqueHSN.map(hsn => {
      const hsnRows = invoicePreviewData.rows.filter(r => r.hsn_sac === hsn);
      const rawSum = hsnRows.reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0);
      const gstRateNum = Number(invoicePreviewData.selectedGstRate) || 0;
      const taxableValue = invoicePreviewData.gstIncluded ? (rawSum / (1 + gstRateNum / 100)) : rawSum;
      const igstRate = invoicePreviewData.igstAmount > 0 ? gstRateNum : 0;
      const sgstRate = invoicePreviewData.sgstAmount > 0 ? (gstRateNum / 2) : 0;
      const utgstRate = sgstRate;
      const igstAmount = igstRate ? (igstRate * taxableValue) / 100 : 0;
      const sgstAmount = sgstRate ? (sgstRate * taxableValue) / 100 : 0;
      const totalTax = igstAmount + 2 * sgstAmount;
      return {
        hsn,
        taxableValue: Math.round(taxableValue),
        igstRate,
        igstAmount: Math.round(igstAmount),
        sgstRate,
        sgstAmount: Math.round(sgstAmount),
        totalTax: Math.round(totalTax)
      };
    });

    const totalTax = result.reduce((sum, row) => sum + row.totalTax, 0);

    return {
      taxRows: result,
      totalTax,
      uniqueHSN
    };
  }

  const amountInWords = invoicePreviewData
    ? `INR ${numberToWords(Number(invoicePreviewData.totalAmount)).replace(/\s+/, ' ').trim()} Only`
    : "";

  const taxTableData = getTaxTableData();
  const taxAmountInWords = taxTableData.totalTax > 0
    ? `INR ${numberToWords(taxTableData.totalTax).replace(/\s+/, ' ').trim()} Only`
    : "";

  // --- UI RENDER ---
  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <div
        className="text-center py-5"
        style={{
          fontSize: '30px',
          fontWeight: 900,
          letterSpacing: '2px',
          color: '#1e3a8a',
          paddingTop: '26px',
          paddingBottom: '20px'
        }}
      >
        {invoiceType === "proforma" ? "PROFORMA INVOICE" : invoiceType === "lut" ? "LUT INVOICE" : invoiceType === "commission" ? "COMMISSION INVOICE" : "TAX INVOICE"}
      </div>
      <h2 className="text-3xl font-extrabold mb-2 text-blue-800 text-center tracking-tight">Invoice Creation Form</h2>
      <p className="text-center text-gray-500 mb-8 text-md">Fill the details below to instantly generate a professional GST invoice.</p>
      {errorMsg && (
        <div className="mb-4 p-2 bg-red-50 text-red-700 rounded border border-red-200 text-center shadow">
          {errorMsg}
        </div>
      )}
      <SuccessActionsPopup
        open={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        goToInvoices={goToInvoices}
        handleAddMoreInvoices={handleAddMoreInvoices}
        viewInvoice={viewInvoice}
        downloadInvoice={downloadInvoice}
        shareInvoice={shareInvoice}
        invoicePreviewData={invoicePreviewData}
      />
      {successMsg && (
        <div className="mb-4 p-2 bg-green-50 text-green-700 rounded border border-green-200 text-center shadow">
          {successMsg}
        </div>
      )}
      {!showSuccessPopup &&
      <form
        onSubmit={handleSubmit}
        className="bg-gradient-to-tr from-white via-blue-50 to-blue-100 shadow-xl border border-blue-200 rounded-3xl p-9 space-y-8 transition-all"
      >
        <div className="grid md:grid-cols-2 gap-7">
          <div>
            <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">
              Select Company <span className="text-pink-600">*</span>
            </label>
            {/* If role is company, select and lock - else, regular select */}
            {isCompanyRole && myCompanyId ? (
              <select
                value={String(myCompanyId)}
                disabled
                className="form-select w-full border-2 border-blue-200 rounded-xl bg-slate-100 text-gray-500 p-2 focus:ring-2 focus:ring-blue-400 transition cursor-not-allowed"
              >
                {companies.filter(c => String(c.id) === String(myCompanyId)).map((c) => (
                  <option value={c.id} key={c.id}>
                    {c.company_name}
                    {c.gst_number ? ` (GST: ${c.gst_number})` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <select
                value={companyId}
                onChange={onCompanyChange}
                required
                className="form-select w-full border-2 border-blue-200 rounded-xl bg-white p-2 focus:ring-2 focus:ring-blue-400 transition"
              >
                <option value="">-- Select Company --</option>
                {companies.map((c) => (
                  <option value={c.id} key={c.id}>
                    {c.company_name}
                    {c.gst_number ? ` (GST: ${c.gst_number})` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">
              Select Buyer <span className="text-pink-600">*</span>
            </label>
            <select
              value={customerId}
              onChange={onCustomerChange}
              required
              className="form-select w-full border-2 border-blue-200 rounded-xl bg-white p-2 focus:ring-2 focus:ring-blue-400 transition"
            >
              <option value="">-- Select Buyer --</option>
              {filteredCustomers.map((c) => (
                <option value={c.id} key={c.id}>
                  {c.company_name}
                  {c.gst_number ? ` (GST: ${c.gst_number})` : ""}
                </option>
              ))}
            </select>
            {/* Above: If companyId equals decodedUserId and userRole=company, will only show buyers added by that userId. */}
          </div>
        </div>
        {/* ...rest of UI unchanged... */}
        <div className="grid md:grid-cols-3 gap-7">
          <div>
            <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">
              Invoice No. <span className="text-pink-600">*</span>
            </label>
            <input
              type="text"
              className={`form-input w-full rounded-xl border-2 border-gray-200 bg-white p-2 ${invoiceNoAuto ? "text-gray-500" : "text-blue-900"}`}
              value={invoiceNo}
              onChange={handleInvoiceNoChange}
              onFocus={() => setInvoiceNoAuto(false)}
              required
              placeholder="Invoice No."
            />
            {
              invoiceNoAuto && invoiceNo &&
              <div className="text-xs text-yellow-700 mt-1">Auto-generated. You can edit Invoice No. if needed.</div>
            }
            {
              !invoiceNoAuto && <div className="text-xs text-blue-700 mt-1">Custom Invoice No. entered by you. <button type="button" className="underline font-semibold" onClick={() => { setInvoiceNo(getDefaultInvoiceNo()); setInvoiceNoAuto(true); }}>Reset to default</button></div>
            }
            {
              invoiceNo && (
                <div className="text-xs text-gray-800 mt-1">
                  <b>Final Invoice No:</b> <span className="inline-block bg-gray-100 rounded px-2 py-[1.5px] font-mono text-[11px]">{composedInvoiceNo || "-"}</span>
                  {selectedCompany && selectedCompany.separatorsign && (
                    <span className="ml-2 text-xs text-gray-500">(Separator used: <span className="font-semibold">{selectedCompany.separatorsign}</span>)</span>
                  )}
                </div>
              )
            }
          </div>
          <div>
            <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">
              Invoice Date <span className="text-pink-600">*</span>
            </label>
            <input
              type="date"
              className="form-input w-full rounded-xl border-2 border-gray-200 bg-white p-2"
              value={invoiceDate}
              onChange={onInvoiceDateChange}
              required
            />
          </div>
          <div>
            <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">
              Invoice Type <span className="text-pink-600">*</span>
            </label>
            <select
              value={invoiceType}
              onChange={(e) => setInvoiceType(e.target.value)}
              className="form-select w-full border-2 border-blue-200 rounded-xl bg-white p-2 focus:ring-2 focus:ring-blue-400 transition"
              required
            >
              <option value="tax">Tax Invoice</option>
              <option value="proforma">Proforma Invoice</option>
              <option value="lut">LUT (GST 0%) Invoice</option>
              <option value="commission">Commission Invoice</option>
            </select>
          </div>
        </div>

        {/* LUT-Specific Fields */}
        {invoiceType === "lut" && (
          <div className="grid md:grid-cols-2 gap-7 p-4 bg-amber-50/60 border border-amber-200 rounded-2xl">
            <div>
              <label className="font-semibold text-amber-900 block mb-2 uppercase tracking-wider text-xs">
                LUT Number <span className="text-pink-600">*</span>
              </label>
              <input
                type="text"
                className="form-input w-full rounded-xl border-2 border-amber-300 bg-white p-2 text-blue-900 focus:ring-2 focus:ring-amber-400"
                value={lutNumber}
                onChange={(e) => setLutNumber(e.target.value)}
                placeholder="e.g. AD090324000123A"
                required
              />
              <p className="text-xs text-amber-700 mt-1 italic">Enter your LUT reference number issued by GST authorities</p>
            </div>
            <div className="flex items-center">
              <div className="bg-amber-100 border border-amber-300 rounded-xl p-3 text-xs text-amber-900">
                <span className="font-bold block mb-1">📋 LUT Invoice Note</span>
                Supply meant for export under Bond or Letter of Undertaking without payment of IGST. GST is automatically set to 0%.
              </div>
            </div>
          </div>
        )}

        {/* Commission Invoice Fields */}
        {invoiceType === "commission" && (
          <div className="grid md:grid-cols-2 gap-7 p-4 bg-purple-50/60 border border-purple-200 rounded-2xl">
            <div>
              <label className="font-semibold text-purple-900 block mb-2 uppercase tracking-wider text-xs">
                Commission Rate (%) <span className="text-pink-600">*</span>
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="any"
                className="form-input w-full rounded-xl border-2 border-purple-300 bg-white p-2 text-blue-900 focus:ring-2 focus:ring-purple-400"
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
                placeholder="e.g. 5"
              />
            </div>
            <div>
              <label className="font-semibold text-purple-900 block mb-2 uppercase tracking-wider text-xs">
                Base Transaction Value (₹)
              </label>
              <input
                type="number"
                min="0"
                step="any"
                className="form-input w-full rounded-xl border-2 border-purple-300 bg-white p-2 text-blue-900 focus:ring-2 focus:ring-purple-400"
                value={commissionBaseAmount}
                onChange={(e) => setCommissionBaseAmount(e.target.value)}
                placeholder="e.g. 100000"
              />
              {commissionRate && commissionBaseAmount && (
                <p className="text-xs text-purple-700 mt-1 font-semibold">
                  Commission Amount: ₹ {((parseFloat(commissionBaseAmount) || 0) * (parseFloat(commissionRate) || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
              )}
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-7">
          <div>
            <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">
              Discount (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="any"
              className="form-input w-full rounded-xl border-2 border-gray-200 bg-white p-2 text-blue-900"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              placeholder="e.g. 5"
            />
          </div>
        </div>
        {/* ...the rest of the form unchanged... */}
        <div className="mt-3 flex items-center gap-3">
          <input
            type="checkbox"
            id="addMonthInInvoiceNo"
            checked={addMonthInInvoiceNo}
            onChange={handleAddMonthToggle}
            className="accent-blue-700 h-5 w-5"
          />
          <label htmlFor="addMonthInInvoiceNo" className="text-blue-900/90 font-medium">
            Include month in Invoice No. <span className="text-xs text-gray-400 italic hidden">(Helps in tracking)</span>
          </label>
        </div>

        {/* REMARKS INPUT */}
        <div>
          <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">
            Remarks (optional)
          </label>
          <textarea
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            className="form-textarea w-full border-2 border-yellow-200 rounded-xl p-2 bg-yellow-50 min-h-[60px]"
            placeholder="Add remarks about this invoice (optional)..."
            rows={3}
          />
        </div>

        {/* SHIP TO ADDRESS INPUT */}
        <div>
          <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">
            Ship To Address (optional)
          </label>
          <textarea
            value={shipToAddress}
            onChange={e => setShipToAddress(e.target.value)}
            className="form-textarea w-full border-2 border-indigo-200 rounded-xl p-2 bg-indigo-50/30 min-h-[60px]"
            placeholder="Enter shipping address if different from billing..."
            rows={3}
          />
        </div>
        {/* ...Invoice Table... */}
        {/* ...unchanged... */}
        <div>
          <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">
            Particulars & Amount <span className="text-pink-600">*</span>
          </label>
          <div className="overflow-x-auto rounded-xl shadow border border-blue-100 bg-white/70">
            <table className="min-w-full rounded-xl">
              <thead>
                <tr className="bg-blue-100">
                  <th className="py-2 px-3 text-left font-bold text-blue-900 w-[50%]">Particulars</th>
                  <th className="py-2 px-3 text-left font-bold text-blue-900 w-[25%]">HSN/SAC</th>
                  <th className="py-2 px-3 text-left font-bold text-blue-900 w-[20%]">Amount (₹)</th>
                  <th className="py-2 px-3 w-[5%]"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="even:bg-blue-50">
                    <td className="p-2">
                      {(() => {
                        const isCustom = row.isCustom === true;
                        const selectValue = isCustom ? "__custom__" : (serviceTypes.some(st => st.name === row.particulars) ? row.particulars : "");
                        return (
                          <div className="space-y-1.5">
                            <select
                              value={selectValue}
                              onChange={(e) => {
                                const val = e.target.value;
                                setRows(prev => {
                                  const newRows = [...prev];
                                  if (val === "__custom__") {
                                    newRows[i] = { ...newRows[i], particulars: "", hsn_sac: "", amount: "", isCustom: true };
                                  } else if (val === "") {
                                    newRows[i] = { ...newRows[i], particulars: "", hsn_sac: "", amount: "", isCustom: false };
                                  } else {
                                    const matched = serviceTypes.find(st => st.name === val);
                                    newRows[i] = {
                                      ...newRows[i],
                                      particulars: val,
                                      hsn_sac: matched?.hsn_code || "",
                                      amount: matched?.price ? String(matched.price) : "",
                                      isCustom: false
                                    };
                                  }
                                  return newRows;
                                });
                              }}
                              className="form-input w-full rounded bg-blue-50/70 border border-blue-200"
                              required
                            >
                              <option value="">-- Select Service --</option>
                              {serviceTypes.map((st) => (
                                <option key={st.id} value={st.name}>
                                  {st.name} (HSN: {st.hsn_code || "N/A"}, Price: ₹{st.price || 0})
                                </option>
                              ))}
                              <option value="__custom__">Custom Service (Not Listed)</option>
                            </select>
                            {isCustom && (
                              <input
                                type="text"
                                className="form-input w-full rounded bg-blue-50/70 border border-blue-200"
                                value={row.particulars}
                                onChange={(e) => {
                                  const textVal = e.target.value;
                                  setRows(prev => {
                                    const newRows = [...prev];
                                    newRows[i] = { ...newRows[i], particulars: textVal };
                                    return newRows;
                                  });
                                }}
                                placeholder="Enter Custom Service Name"
                                required
                              />
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-2">
                      {(() => {
                        const isLocked = row.isCustom !== true && serviceTypes.some(st => st.name === row.particulars) && row.particulars !== "";
                        return (
                          <>
                            <input
                              type="text"
                              list={`hsn-datalist-${i}`}
                              className={`form-input w-full rounded border ${isLocked ? 'bg-gray-100 text-gray-500 border-gray-200' : 'bg-blue-50/70 border-blue-200'}`}
                              value={row.hsn_sac}
                              onChange={(e) => setRows(prev => {
                                const newRows = [...prev];
                                newRows[i] = { ...newRows[i], hsn_sac: e.target.value };
                                return newRows;
                              })}
                              required
                              placeholder="HSN/SAC Code"
                              disabled={isLocked}
                            />
                            {!isLocked && (
                              <datalist id={`hsn-datalist-${i}`}>
                                {companyHSNOptions.map((h, idx) => (
                                  <option key={h + idx} value={h}>
                                    {h}
                                  </option>
                                ))}
                              </datalist>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td className="p-2">
                      {(() => {
                        const isLocked = row.isCustom !== true && serviceTypes.some(st => st.name === row.particulars) && row.particulars !== "";
                        return (
                          <div className="flex flex-col gap-1">
                            <input
                              type="number"
                              className={`form-input w-full rounded border ${isLocked ? 'bg-gray-100 text-gray-500 border-gray-200' : 'bg-blue-50/70 border-blue-200'}`}
                              value={row.amount}
                              onChange={(e) => setRows(prev => {
                                const newRows = [...prev];
                                newRows[i] = { ...newRows[i], amount: e.target.value };
                                return newRows;
                              })}
                              min="0"
                              step="any"
                              required
                              placeholder="0.00"
                              disabled={isLocked}
                            />
                            {gstIncluded && selectedGstRate && Number(selectedGstRate) > 0 && parseFloat(row.amount) > 0 && (
                              <span className="text-[10px] text-green-700 font-bold px-1">
                                Taxable: ₹ {((parseFloat(row.amount) || 0) / (1 + Number(selectedGstRate) / 100)).toFixed(2)}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-2 text-center align-middle">
                      {rows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setRows(prev => prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i))}
                          className="text-red-500 bg-red-50 hover:bg-red-100 rounded-full px-3 py-1 font-bold shadow-sm transition"
                          title="Remove row"
                          tabIndex={-1}
                        >
                          &times;
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            onClick={() => setRows(prev => [...prev, { ...initialInvoiceRow }])}
            className="mt-3 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl shadow hover:from-blue-500 hover:to-blue-300 transition font-semibold"
          >
            + Add More
          </button>
        </div>
        {gstSlabOptions.length > 0 && (
          <div>
            <label className="font-semibold text-blue-900 block mb-2 mt-1 uppercase tracking-wider">
              Select GST Rate <span className="text-pink-600">*</span>
            </label>
            <select
              value={selectedGstRate}
              onChange={onGstRateChange}
              required
              className="form-select w-full border-2 border-green-300 rounded-xl bg-white p-2 focus:ring-2 focus:ring-green-400 transition"
            >
              <option value="">-- Select GST Rate --</option>
              {gstSlabOptions.map((rate, idx) => (
                <option value={rate} key={rate + idx}>{rate}%</option>
              ))}
            </select>
            <div className="mt-3 flex items-center gap-3">
              <input
                type="checkbox"
                id="gstIncludedToggle"
                checked={gstIncluded}
                onChange={(e) => setGstIncluded(e.target.checked)}
                className="accent-blue-700 h-5 w-5"
              />
              <label htmlFor="gstIncludedToggle" className="text-blue-900/90 font-medium">
                Amount includes GST
              </label>
            </div>
          </div>
        )}
        {/* --- Tax and total preview, submit, etc. unchanged --- */}
        <div className="grid md:grid-cols-2 gap-6 bg-gradient-to-br from-white to-blue-50 rounded-2xl p-5 border border-blue-100 shadow">
          <div>
            <div className="mb-3 flex justify-between border-b pb-2">
              <span className="text-blue-700 font-medium">
                SGST ({selectedGstRate ? `${(Number(selectedGstRate) / 2)}%`  : "0%"})
              </span>
              <span className="text-blue-900 font-semibold">₹ {sgstAmount}</span>
            </div>
            <div className="mb-3 flex justify-between border-b pb-2">
              <span className="text-blue-700 font-medium">
                CGST ({selectedGstRate ? `${(Number(selectedGstRate) / 2)}%`  : "0%"})
              </span>
              <span className="text-blue-900 font-semibold">₹ {cgstAmount}</span>
            </div>
            <div className="mb-3 flex justify-between border-b pb-2">
              <span className="text-blue-700 font-medium">
                IGST ({selectedGstRate ? `${Number(selectedGstRate)}%` : "0%"})
              </span>
              <span className="text-blue-900 font-semibold">₹ {igstAmount}</span>
            </div>
            <div className="font-extrabold flex items-center justify-between text-xl pt-3 text-green-800">
              <span>Total</span>
              <span>₹ {totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
          <div className="flex flex-col justify-center items-end text-xs text-gray-400 italic hidden">
            <span>
              <span className="font-semibold text-blue-700">* Tax type</span> auto-based on state: same = SGST/CGST, different = IGST.
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-6 pt-4 justify-center">
          <button
            type="submit"
            className={`px-7 py-2.5 rounded-2xl bg-gradient-to-r from-green-500 to-green-600 text-white font-bold shadow hover:from-green-400 hover:to-green-600 transition text-lg ${
              submitLoading ? "opacity-70 cursor-not-allowed" : ""
            }`}
            disabled={submitLoading}
          >
            {submitLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5 mr-1 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                </svg>
                Generating...
              </span>
            ) : (
              <>Generate Invoice</>
            )}
          </button>
          <button
            type="button"
            className="px-7 py-2.5 rounded-2xl bg-gradient-to-l from-blue-700 to-blue-500 text-white font-bold shadow hover:from-blue-600 hover:to-blue-400 transition text-lg"
            onClick={handlePreview}
          >
            Preview Invoice
          </button>
        </div>
      </form>
      }
      {showInvoice && invoicePreviewData && (
        <>
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-gradient-to-tr from-white to-blue-50 w-[820px] max-h-[96vh] overflow-auto rounded-2xl shadow-2xl p-3 border border-blue-200">
              <div className="flex justify-end gap-5 mb-2">
                <button
                  onClick={handlePrint}
                  className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-3 py-2 rounded-xl shadow hover:from-blue-400 hover:to-blue-200 transition text-[13px]"
                >
                  Print
                </button>
                <button
                  onClick={() => setShowInvoice(false)}
                  className="bg-gray-500 hover:bg-gray-800 text-white px-3 py-2 rounded-xl shadow transition text-[13px]"
                >
                  Close
                </button>
              </div>
              <InvoicePreviewContent
                companies={companies}
                companySignatureUrls={companySignatureUrls}
                customers={customers}
                invoicePreviewData={invoicePreviewData}
              />
            </div>
          </div>
        </>
      )}
      {/* Hidden Invoice Preview for background PDF generation */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', pointerEvents: 'none' }}>
        {companyId && customerId && invoiceDate && (
          <div id="invoice-print-hidden" style={{ width: '794px', padding: '15px', boxSizing: 'border-box', background: 'white' }}>
            <InvoicePreviewContent
              companies={companies}
              companySignatureUrls={companySignatureUrls}
              customers={customers}
              invoicePreviewData={{
                companyId,
                customerId,
                invoiceNo: composedInvoiceNo,
                invoiceDate,
                rows,
                sgstAmount,
                cgstAmount,
                igstAmount,
                totalAmount,
                selectedGstRate,
                remarks: remarks.trim() ? remarks.trim() : undefined,
                composedInvoiceNo: composedInvoiceNo,
                bankDetails: bankDetails,
                shipToAddress: shipToAddress.trim() || undefined,
                gstIncluded,
                invoice_type: invoiceType,
                lut_number: invoiceType === 'lut' ? lutNumber : undefined,
                commission_rate: invoiceType === 'commission' ? commissionRate : undefined,
                commission_base_amount: invoiceType === 'commission' ? commissionBaseAmount : undefined,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceCreator;